import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Bus, MapPin, Users, Clock, MessageCircle, CheckCircle2, ShieldCheck, Phone } from "lucide-react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { resolveImageUrl } from "../lib/imageUrl";

const TransferCard = ({ t, settings, formatPrice }) => {
    const wa = (settings.whatsapp_number || "905555555555").replace(/\D/g, "");
    const message = encodeURIComponent(
        `Merhaba, ${t.name} (${t.route || ""}) transferi için fiyat ve müsaitlik bilgisi alabilir miyim?`
    );
    const waHref = `https://wa.me/${wa}?text=${message}`;
    const img = t.image || (t.images || [])[0];

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.4 }}
            className="bg-emerald_surface border border-border_green overflow-hidden flex flex-col"
            data-testid={`transfer-card-${t.id}`}
        >
            <div className="aspect-[16/10] bg-emerald_deep relative overflow-hidden">
                {img ? (
                    <img src={resolveImageUrl(img)} alt={t.name} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted_green">
                        <Bus className="w-16 h-16" />
                    </div>
                )}
                <div className="absolute top-3 left-3 bg-emerald_deep/85 backdrop-blur px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-gold border border-gold/40">
                    {t.vehicle_type || "Otobüs"}
                </div>
                {t.capacity > 0 && (
                    <div className="absolute top-3 right-3 bg-emerald_deep/85 backdrop-blur px-2 py-1 text-[10px] text-bone/90 flex items-center gap-1">
                        <Users className="w-3 h-3 text-gold" /> {t.capacity} kişi
                    </div>
                )}
            </div>

            <div className="p-5 flex-1 flex flex-col">
                <h3 className="font-serif text-xl text-bone leading-tight">{t.name}</h3>
                {t.route && (
                    <div className="flex items-center gap-1.5 text-xs text-muted_green mt-1.5">
                        <MapPin className="w-3.5 h-3.5 text-gold" /> {t.route}
                    </div>
                )}
                {t.duration && (
                    <div className="flex items-center gap-1.5 text-xs text-muted_green mt-1">
                        <Clock className="w-3.5 h-3.5 text-gold" /> {t.duration}
                    </div>
                )}

                {t.description && (
                    <p className="text-sm text-bone/80 mt-3 leading-relaxed line-clamp-4">{t.description}</p>
                )}

                {(t.features || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                        {(t.features || []).slice(0, 8).map((f, i) => (
                            <span key={i} className="text-[10px] uppercase tracking-[0.15em] text-muted_green bg-emerald_deep/40 border border-border_green px-2 py-0.5">
                                <CheckCircle2 className="w-2.5 h-2.5 inline -mt-0.5 text-gold mr-1" />{f}
                            </span>
                        ))}
                    </div>
                )}

                <div className="mt-auto pt-5 grid grid-cols-2 gap-3 items-end border-t border-border_green mt-5">
                    <div>
                        {Number(t.price_per_person) > 0 && (
                            <>
                                <div className="text-[10px] uppercase tracking-[0.18em] text-muted_green">Kişi Başı</div>
                                <div className="text-gold font-bold text-xl font-mono">{formatPrice(t.price_per_person)}</div>
                            </>
                        )}
                        {Number(t.price_private) > 0 && (
                            <div className="text-[11px] text-bone/70 mt-1">Özel: <span className="text-gold font-semibold">{formatPrice(t.price_private)}</span></div>
                        )}
                    </div>
                    <a
                        href={waHref}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-gold !text-xs justify-center"
                        data-testid={`transfer-wa-${t.id}`}
                    >
                        <MessageCircle className="w-3.5 h-3.5" /> Sor / Rezerve Et
                    </a>
                </div>
            </div>
        </motion.div>
    );
};

const Transfers = () => {
    const { settings, formatPrice } = useApp();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        api.get("/transfers").then(({ data }) => { if (alive) setItems(data || []); }).finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    return (
        <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16"
            data-testid="transfers-page"
        >
            <div className="text-center mb-12">
                <div className="text-xs uppercase tracking-[0.35em] text-gold mb-3">TRANSFER & ULAŞIM</div>
                <h1 className="font-serif text-4xl sm:text-5xl text-bone tracking-tight">Otobüs ve VIP Transfer</h1>
                <p className="text-bone/70 mt-4 max-w-2xl mx-auto leading-relaxed">
                    Cidde Havalimanı'ndan otelinize, Mekke-Medine arası ve Harem-otel arası konforlu, klimalı transfer hizmetlerimiz.
                </p>
                <div className="flex items-center justify-center gap-6 mt-6 flex-wrap">
                    <div className="flex items-center gap-2 text-xs text-muted_green">
                        <ShieldCheck className="w-4 h-4 text-gold" /> Profesyonel şoför
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted_green">
                        <Phone className="w-4 h-4 text-gold" /> 7/24 destek
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="text-center text-muted_green py-12">Yükleniyor…</div>
            ) : items.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border_green">
                    <Bus className="w-12 h-12 text-muted_green mx-auto mb-3" />
                    <div className="text-muted_green">Henüz aktif transfer eklenmemiş. WhatsApp üzerinden bizimle iletişime geçin.</div>
                </div>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="transfer-grid">
                    {items.map((t) => <TransferCard key={t.id} t={t} settings={settings} formatPrice={formatPrice} />)}
                </div>
            )}
        </motion.section>
    );
};

export default Transfers;
